import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErrorCode } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrentPartner } from "@/hooks/useCurrentPartner";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateRangePicker, type DateRange } from "@/components/DateRangePicker";
import { EditorialButton, SearchInput } from "@/components/editorial";
import { ColumnInfo, DashCard, DashHeading, MetricCard, PillTabs } from "@/components/dashboard";
import { formatDateTZ, formatDuration, formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

type Subscription = {
  id: number;
  sold_to_email: string;
  sold_to_name: string;
  duration_days: number;
  currency: string;
  price_cents: number;
  status: "active" | "paused" | "cancelled";
  is_redeemed: boolean;
  next_renewal_at: string | null;
  current_access_ends_at: string | null;
  current_grant_status: string | null;
  renewals_count: number;
  created_at: string;
  cancelled_at: string | null;
  cancelled_reason: string | null;
};

type SubscriptionList = { items: Subscription[]; total: number };
type SubscriptionCounts = {
  active: number;
  pending_redemption: number;
  paused: number;
  cancelled: number;
};

type FilterDef = {
  value: string;
  label: string;
  countKey?: keyof SubscriptionCounts;
  apiParams: { status?: "active" | "paused" | "cancelled"; redeemed?: boolean };
};

function defaultRange(): DateRange {
  const d = new Date();
  const from = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  const to = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10);
  return { from, to };
}

function rangeToUtc(range: DateRange): { from: string; to: string } {
  const [fy, fm, fd] = range.from.split("-").map(Number);
  const [ty, tm, td] = range.to.split("-").map(Number);
  const fromLocal = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
  const toLocal = new Date(ty, tm - 1, td, 23, 59, 59, 999);
  return { from: fromLocal.toISOString(), to: toLocal.toISOString() };
}

const FILTERS: FilterDef[] = [
  { value: "", label: "Todas", apiParams: {} },
  { value: "active", label: "Activas", countKey: "active", apiParams: { status: "active", redeemed: true } },
  {
    value: "pending",
    label: "Pendientes",
    countKey: "pending_redemption",
    apiParams: { status: "active", redeemed: false },
  },
  { value: "paused", label: "Pausadas", countKey: "paused", apiParams: { status: "paused" } },
  { value: "cancelled", label: "Canceladas", countKey: "cancelled", apiParams: { status: "cancelled" } },
];

export default function SubscriptionsPage() {
  const { data: partner } = useCurrentPartner();
  const role = useAuth((s) => s.user?.role);
  const canWrite = role === "owner" || role === "staff";
  const [filterValue, setFilterValue] = useState<string>("");
  const [search, setSearch] = useState("");
  const [range, setRange] = useState<DateRange>(defaultRange());

  const utc = rangeToUtc(range);

  const { data, isLoading } = useQuery<SubscriptionList>({
    queryKey: ["subscriptions", filterValue, search, range.from, range.to],
    queryFn: async () => {
      const active = FILTERS.find((f) => f.value === filterValue) ?? FILTERS[0];
      const params: Record<string, string | number | boolean> = {
        limit: 200,
        from: utc.from,
        to: utc.to,
      };
      if (active.apiParams.status) params.status = active.apiParams.status;
      if (active.apiParams.redeemed !== undefined) params.redeemed = active.apiParams.redeemed;
      if (search.trim()) params.q = search.trim();
      return (await api.get<SubscriptionList>("/partner/subscriptions", { params })).data;
    },
    enabled: !!partner,
  });

  const { data: counts } = useQuery<SubscriptionCounts>({
    queryKey: ["subscription-counts"],
    queryFn: async () => (await api.get<SubscriptionCounts>("/partner/subscriptions/counts")).data,
    enabled: !!partner,
  });

  if (!partner) return <div className="text-sm text-neutral-500">Cargando…</div>;

  const filterOptions = FILTERS.map((f) => ({
    value: f.value,
    label: f.label,
    count: counts && f.countKey ? counts[f.countKey] : undefined,
  }));

  return (
    <div>
      <DashHeading
        title="Suscripciones"
        description="Suscripciones recurrentes emitidas en el período seleccionado. Cada fila es una relación comercial que se renueva automáticamente. Puedes pausar, reactivar o cancelar desde acá. Los códigos individuales (incluyendo cada renovación) los ves en la sección Códigos."
        actions={<DateRangePicker value={range} onChange={setRange} />}
      />

      {/* KPI resumen */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <MetricCard
          title="Activas"
          value={counts ? counts.active.toLocaleString() : "—"}
          hint="Canjeadas y con acceso real"
        />
        <MetricCard
          title="Pendientes de canje"
          value={counts ? counts.pending_redemption.toLocaleString() : "—"}
          hint="Emitidas pero el cliente aún no las activó"
        />
        <MetricCard
          title="Pausadas"
          value={counts ? counts.paused.toLocaleString() : "—"}
          hint="Detenidas temporalmente, se pueden reactivar"
        />
        <MetricCard
          title="Canceladas"
          value={counts ? counts.cancelled.toLocaleString() : "—"}
          hint="Terminadas de forma permanente"
        />
      </div>

      <DashCard bodyClassName="p-0">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-neutral-200">
          <PillTabs value={filterValue} onChange={setFilterValue} options={filterOptions} />
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar por nombre o email…"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50/50 border-b border-neutral-200">
              <tr className="text-left text-xs font-medium text-neutral-600">
                <th className="px-6 py-3">Cliente</th>
                <th className="px-6 py-3">Plan</th>
                <th className="px-6 py-3">Ciclos</th>
                <th className="px-6 py-3">Próxima renovación</th>
                <th className="px-6 py-3">Acceso vigente</th>
                <th className="px-6 py-3">
                  <span className="inline-flex items-center gap-1.5">
                    Estado
                    <ColumnInfo
                      title="Estados de una suscripción"
                      items={[
                        { label: "Activa", description: "Redimida por el cliente y renovándose automáticamente. El cliente tiene acceso PRO vigente." },
                        { label: "Pendiente de canje", description: "Emitida pero el cliente aún no activó el código en la app. El período de acceso arranca cuando lo canjea. Puedes cancelarla si el cliente ya no la va a usar." },
                        { label: "Pausada", description: "Detenida temporalmente. No se genera un nuevo ciclo hasta que la reactives. El acceso vigente se mantiene hasta su vencimiento natural." },
                        { label: "Cancelada", description: "Terminada de forma permanente. No se pueden generar más ciclos. Si al cancelar marcaste el checkbox 'revocar acceso', el cliente ya no tiene PRO." },
                      ]}
                    />
                  </span>
                </th>
                {canWrite && (
                  <th className="px-6 py-3 text-right">
                    <span className="inline-flex items-center gap-1.5 justify-end">
                      Acciones
                      <ColumnInfo
                        title="Acciones disponibles"
                        items={[
                          { label: "Pausar", description: "Detiene las renovaciones automáticas. El acceso PRO vigente se mantiene hasta su vencimiento natural. Puede reactivarse después." },
                          { label: "Reactivar", description: "Vuelve a activar una suscripción pausada. Retoma las renovaciones automáticas desde el próximo ciclo." },
                          { label: "Cancelar", description: "Detiene las renovaciones permanentemente. Por default el acceso PRO vigente se mantiene hasta expirar naturalmente. Marca el checkbox 'revocar también el acceso' para cortarlo de inmediato. Requiere motivo." },
                        ]}
                      />
                    </span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {isLoading && (
                <tr>
                  <td colSpan={canWrite ? 7 : 6} className="px-6 py-8 text-sm text-neutral-500">
                    Cargando…
                  </td>
                </tr>
              )}
              {data?.items.map((s) => (
                <tr key={s.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-6 py-3">
                    <div className="text-sm text-neutral-900">{s.sold_to_name}</div>
                    <div className="text-xs text-neutral-500">{s.sold_to_email}</div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-sm text-neutral-900">{formatDuration(s.duration_days)}</div>
                    <div className="text-xs text-neutral-500 tabular-nums">
                      {formatMoney(s.price_cents, s.currency)} · {s.duration_days} días
                    </div>
                  </td>
                  <td className="px-6 py-3 tabular-nums text-sm text-neutral-900">
                    {s.renewals_count + 1}
                    <span className="text-xs text-neutral-500 ml-1">
                      {s.renewals_count === 0 ? "(inicial)" : `(${s.renewals_count} renov)`}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-xs text-neutral-600">
                    {s.status === "active" && s.next_renewal_at
                      ? formatDateTZ(s.next_renewal_at, partner.timezone)
                      : s.status === "paused"
                      ? "—"
                      : "—"}
                  </td>
                  <td className="px-6 py-3">
                    <AccessCell
                      endsAt={s.current_access_ends_at}
                      grantStatus={s.current_grant_status}
                      partnerTz={partner.timezone}
                    />
                  </td>
                  <td className="px-6 py-3">
                    <SubscriptionStatusBadge status={s.status} isRedeemed={s.is_redeemed} />
                  </td>
                  {canWrite && (
                    <td className="px-6 py-3 text-right whitespace-nowrap space-x-2">
                      <SubscriptionActions sub={s} />
                    </td>
                  )}
                </tr>
              ))}
              {data && data.items.length === 0 && (
                <tr>
                  <td colSpan={canWrite ? 7 : 6} className="px-6 py-10 text-center text-sm text-neutral-500">
                    {search.trim()
                      ? `Sin resultados para "${search.trim()}".`
                      : "Sin suscripciones en esta vista."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </DashCard>
    </div>
  );
}

/* ─────────────── Cells ─────────────── */

function AccessCell({
  endsAt,
  grantStatus,
  partnerTz,
}: {
  endsAt: string | null;
  grantStatus: string | null;
  partnerTz: string;
}) {
  if (!endsAt || !grantStatus) return <span className="text-neutral-400">—</span>;

  const dot =
    grantStatus === "active"
      ? "bg-emerald-500"
      : grantStatus === "expired"
      ? "bg-neutral-400"
      : "bg-red-500";
  const label =
    grantStatus === "active" ? "Vigente" : grantStatus === "expired" ? "Expirado" : "Revocado";

  return (
    <div className="text-xs space-y-0.5">
      <div className="inline-flex items-center gap-1.5">
        <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
        <span className="text-neutral-900 font-medium">{label}</span>
      </div>
      <div className="text-neutral-500">
        {grantStatus === "active" ? "Hasta " : ""}
        {formatDateTZ(endsAt, partnerTz, "dd MMM")}
      </div>
    </div>
  );
}

function SubscriptionStatusBadge({
  status,
  isRedeemed,
}: {
  status: "active" | "paused" | "cancelled";
  isRedeemed: boolean;
}) {
  // Pendiente de canje = status='active' AND !isRedeemed
  const effective =
    status === "active" && !isRedeemed ? ("pending" as const) : status;

  const config = {
    active: { label: "Activa", cls: "bg-emerald-50 text-emerald-700 border-emerald-100" },
    pending: { label: "Pendiente de canje", cls: "bg-blue-50 text-blue-700 border-blue-100" },
    paused: { label: "Pausada", cls: "bg-amber-50 text-amber-700 border-amber-100" },
    cancelled: { label: "Cancelada", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  }[effective];

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        config.cls
      )}
    >
      {config.label}
    </span>
  );
}

/* ─────────────── Row actions ─────────────── */

function SubscriptionActions({ sub }: { sub: Subscription }) {
  const qc = useQueryClient();
  const [pauseOpen, setPauseOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [revokeGrant, setRevokeGrant] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ["subscriptions"] });
    qc.invalidateQueries({ queryKey: ["subscription-counts"] });
    qc.invalidateQueries({ queryKey: ["codes"] });
  }

  const pause = useMutation({
    mutationFn: async () =>
      (await api.post(`/partner/subscriptions/${sub.id}/pause`, { reason: reason || null })).data,
    onSuccess: () => {
      invalidateAll();
      setPauseOpen(false);
      setReason("");
      toast.success("Suscripción pausada");
    },
    onError: () => {
      setErr("No se pudo pausar la suscripción");
      toast.error("No se pudo pausar la suscripción");
    },
  });

  const reactivate = useMutation({
    mutationFn: async () =>
      (await api.post(`/partner/subscriptions/${sub.id}/reactivate`, {})).data,
    onSuccess: () => {
      invalidateAll();
      toast.success("Suscripción reactivada");
    },
    onError: () => toast.error("No se pudo reactivar la suscripción"),
  });

  const cancel = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/partner/subscriptions/${sub.id}/cancel`, {
          reason,
          revoke_active_grant: revokeGrant,
        })
      ).data,
    onSuccess: () => {
      invalidateAll();
      setCancelOpen(false);
      setReason("");
      setRevokeGrant(false);
      toast.success(
        revokeGrant
          ? "Suscripción cancelada y acceso revocado"
          : "Suscripción cancelada"
      );
    },
    onError: (e) => {
      const c = apiErrorCode(e);
      const msg =
        c === "SUBSCRIPTION_ALREADY_CANCELLED" ? "Ya estaba cancelada" : "No se pudo cancelar";
      setErr(msg);
      toast.error(msg);
    },
  });

  if (sub.status === "cancelled") {
    return <span className="text-xs text-neutral-400">—</span>;
  }

  return (
    <>
      {sub.status === "active" && (
        <EditorialButton
          variant="ghost"
          size="sm"
          onClick={() => {
            setErr(null);
            setPauseOpen(true);
          }}
        >
          Pausar
        </EditorialButton>
      )}
      {sub.status === "paused" && (
        <EditorialButton
          variant="ghost"
          size="sm"
          disabled={reactivate.isPending}
          onClick={() => reactivate.mutate()}
        >
          {reactivate.isPending ? "…" : "Reactivar"}
        </EditorialButton>
      )}
      <EditorialButton
        variant="ghost"
        size="sm"
        onClick={() => {
          setErr(null);
          setCancelOpen(true);
        }}
      >
        Cancelar
      </EditorialButton>

      {/* Diálogo Pausar */}
      <Dialog open={pauseOpen} onOpenChange={setPauseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold tracking-tight">Pausar suscripción</DialogTitle>
            <DialogDescription>
              Se detiene la renovación automática. El acceso PRO vigente se mantiene hasta que expire naturalmente. Puede reactivarse después.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Label className="text-sm font-medium text-neutral-700">Motivo (opcional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={255} />
            {err && <p className="text-sm text-destructive">{err}</p>}
          </div>
          <DialogFooter className="mt-4 gap-2">
            <DialogClose asChild>
              <EditorialButton variant="ghost">Cerrar</EditorialButton>
            </DialogClose>
            <EditorialButton variant="primary" disabled={pause.isPending} onClick={() => pause.mutate()}>
              {pause.isPending ? "Pausando" : "Pausar"}
            </EditorialButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo Cancelar */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold tracking-tight">Cancelar suscripción</DialogTitle>
            <DialogDescription>
              Detiene las renovaciones permanentemente. Por default el acceso PRO vigente se mantiene hasta la fecha natural de vencimiento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-neutral-700">Motivo</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={255} />
            </div>
            <label className="flex items-start gap-2 text-sm rounded-md border border-neutral-200 p-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-neutral-900"
                checked={revokeGrant}
                onChange={(e) => setRevokeGrant(e.target.checked)}
              />
              <span>
                Revocar también el acceso PRO vigente
                <span className="block text-xs text-neutral-500">
                  Corta el acceso del cliente <strong>de inmediato</strong>, sin esperar al vencimiento natural.
                </span>
              </span>
            </label>
            {err && <p className="text-sm text-destructive">{err}</p>}
          </div>
          <DialogFooter className="mt-4 gap-2">
            <DialogClose asChild>
              <EditorialButton variant="ghost">Cerrar</EditorialButton>
            </DialogClose>
            <EditorialButton
              variant="primary"
              disabled={!reason.trim() || cancel.isPending}
              onClick={() => cancel.mutate()}
            >
              {cancel.isPending ? "Cancelando" : "Cancelar suscripción"}
            </EditorialButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
